<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Edits the per-subcategory required-document checklist template, stored in
// workflow_config.document_requests as [{ label, document_type, required, parts }].
// `parts` is an optional list of named sides (e.g. an INE's Frente + Reverso) — empty
// means a normal single-file document. Tickets created in this subcategory seed their
// checklist from these rows.
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const TYPE_KEYS = [
  'acta_constitutiva',
  'poder_notarial',
  'nda',
  'contrato',
  'propuesta',
  'factura',
  'identificacion',
  'otro',
];

const rows = computed({
  get: () => props.modelValue || [],
  set: value => emit('update:modelValue', value),
});

const addRow = () => {
  rows.value = [
    ...rows.value,
    { label: '', document_type: 'otro', required: true },
  ];
};

const updateRow = (index, patch) => {
  rows.value = rows.value.map((row, idx) =>
    idx === index ? { ...row, ...patch } : row
  );
};

const removeRow = index => {
  rows.value = rows.value.filter((_, idx) => idx !== index);
};

// --- Sides / parts (e.g. an INE's Frente + Reverso) ---
const partsOf = row => (Array.isArray(row.parts) ? row.parts : []);

const addPart = index => {
  const parts = [...partsOf(rows.value[index]), { label: '' }];
  updateRow(index, { parts });
};

const updatePart = (index, partIndex, label) => {
  const parts = partsOf(rows.value[index]).map((part, idx) =>
    idx === partIndex ? { ...part, label } : part
  );
  updateRow(index, { parts });
};

const removePart = (index, partIndex) => {
  const parts = partsOf(rows.value[index]).filter((_, idx) => idx !== partIndex);
  updateRow(index, { parts });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-xs text-n-slate-10">
      {{ t('DOCUMENT_REQUEST_TEMPLATES.HINT') }}
    </p>

    <div
      v-for="(row, index) in rows"
      :key="index"
      class="flex flex-col gap-2 p-2 border rounded-md border-n-weak/60"
    >
      <div class="flex items-center gap-2">
        <input
          :value="row.label"
          type="text"
          :placeholder="t('DOCUMENT_REQUEST_TEMPLATES.LABEL_PLACEHOLDER')"
          class="flex-1 min-w-0 h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
          @input="updateRow(index, { label: $event.target.value })"
        />
        <select
          :value="row.document_type || 'otro'"
          class="h-8 mb-0 px-2 text-sm border rounded-md outline-0 reset-base shrink-0 w-28 bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          @change="updateRow(index, { document_type: $event.target.value })"
        >
          <option v-for="key in TYPE_KEYS" :key="key" :value="key">
            {{ t(`PROJECTS.DOCUMENTS.TYPES.${key}`) }}
          </option>
        </select>
        <input
          v-tooltip.top="t('DOCUMENT_REQUEST_TEMPLATES.REQUIRED')"
          :checked="row.required !== false"
          type="checkbox"
          class="shrink-0"
          :aria-label="t('DOCUMENT_REQUEST_TEMPLATES.REQUIRED')"
          @change="updateRow(index, { required: $event.target.checked })"
        />
        <Button
          type="button"
          variant="ghost"
          color="ruby"
          size="xs"
          icon="i-lucide-x"
          @click="removeRow(index)"
        />
      </div>

      <!-- Sides / parts: optional named slots for a multi-side document (e.g. INE). -->
      <div class="flex flex-col gap-1.5 pl-1">
        <p class="text-xs text-n-slate-10">{{ t('DOCUMENT_REQUEST_TEMPLATES.SIDES_HINT') }}</p>
        <div
          v-for="(part, partIndex) in partsOf(row)"
          :key="partIndex"
          class="flex items-center gap-2"
        >
          <input
            :value="part.label"
            type="text"
            :placeholder="t('DOCUMENT_REQUEST_TEMPLATES.SIDE_PLACEHOLDER')"
            class="flex-1 min-w-0 h-7 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
            @input="updatePart(index, partIndex, $event.target.value)"
          />
          <Button
            type="button"
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-x"
            @click="removePart(index, partIndex)"
          />
        </div>
        <div>
          <Button
            type="button"
            variant="ghost"
            color="slate"
            size="xs"
            icon="i-lucide-plus"
            :label="t('DOCUMENT_REQUEST_TEMPLATES.ADD_SIDE')"
            @click="addPart(index)"
          />
        </div>
      </div>
    </div>

    <div>
      <Button
        type="button"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-plus"
        :label="t('DOCUMENT_REQUEST_TEMPLATES.ADD')"
        @click="addRow"
      />
    </div>
  </div>
</template>
